import * as React from "react";
import {SyntheticEvent} from "react";

import "./layer-list-item.scss";

import {LayerListList} from "../layer-list-list";
import {LayerListConfiguration} from "../../../../config/layer-list-configuration";

import {Button, Checkbox, CheckboxProps, Radio} from "semantic-ui-react";
import {Icon} from "../../../icon/icon";
import {MetadataConfiguration} from "../../../../config/metadata-configuration";
import {SidebarControlsConfiguration} from "../../../../../config/sidebar-configuration";
import {Legend} from "../../../legend/legend";
import {Legend as CustomLegend} from "../../../../../components/app/sidebar/legend/legend";
import {PathUtils} from "../../../../../utils/path-utils";
import WatchHandle = __esri.WatchHandle;
import _ from "lodash";
import ListItem from "@arcgis/core/widgets/LayerList/ListItem";
import Collection from "@arcgis/core/core/Collection";
import {autorun} from "mobx";
import {IAppStore, withStore} from "../../../../stores/abstract-app-store";


export interface LayerListItemProps {
    config: LayerListConfiguration;
    subitems?: Collection<ListItem>;
    data: __esri.ListItem;
    metadata: MetadataConfiguration;
    isRoot: boolean;
    level: number;
    onClick: (event: SyntheticEvent, data: CheckboxProps, item: __esri.ListItem) => any;
    controlsConfig: SidebarControlsConfiguration;
    small?: boolean;
    store?: IAppStore;
}

class LayerListItemBase extends React.Component<LayerListItemProps, any> {
    autorunDisposers: any[] = [];
    watchHandles: WatchHandle[] = [];
    metauid: string;
    constructor(props: LayerListItemProps) {
        super(props);

        this.state = {
            visible: props.data.layer.visible,
            visibleAtCurrentScale: props.data.visibleAtCurrentScale,
            open: props.data.open,
            metauid: null,
            hidden: props.data.hidden
        };
        this.handleLayerToggle = this.handleLayerToggle.bind(this);
        this.handleCheckboxChange = this.handleCheckboxChange.bind(this);
    }

    /**
     * Toggles visibility of sub layers
     */
    handleLayerToggle(): void {
        const {data} = this.props;
        this.setState((state: any) => {
            data.open = !state.open;
            return {
                open: !state.open
            };
        });

    }

    /**
     * Handle checkbox change
     *
     * @param event
     * @param eventData
     * @param item
     */
    handleCheckboxChange(event: SyntheticEvent, eventData: CheckboxProps, item: __esri.ListItem): void {
        event.preventDefault();
        // item is part of the top level state
        // --> call callback function to register changes on level of layer list
        if (this.props.onClick) {
            this.props.onClick(event, eventData, item);
        }

        const visible = eventData["checked"];
        // set internal visibility state
        this.setState({
            visible
        });
    }

    handleItemChange(data: __esri.ListItem): void {
        const {metadata} = this.props;
        if (!data || !data.layer) {
            return;
        }
        const metauid = (data.layer as any)?.metauid;
        if (typeof metauid === "string") {
            this.setState({metauid});
        } else {
            const url = PathUtils.getLayerPath(data.layer);
            // metadate Configuration überprüfen
            if (typeof url === "string") {
                const pathUid: string | null = PathUtils.getMetaId(url);
                if (pathUid) {
                    let uid: string | null = null;
                    const metadateConfig = metadata.metauid[pathUid];
                    if (typeof metadateConfig === "string") {
                        uid = metadateConfig;
                    } else if (Array.isArray(metadateConfig) || typeof metadateConfig === "object") {
                        let layerId: number | undefined | null = (data.layer as any).layerId;
                        if (!layerId) {
                            layerId = PathUtils.getLayerId(url);
                        }
                        if (layerId && typeof metadateConfig[layerId] === "string") {
                            uid = metadateConfig[layerId];
                        }
                    }
                    if (uid === "") {
                        uid = null;
                    }
                    this.setState({metauid: uid});
                }
            } else {
                if (typeof metadata.metauid[data.title] !== "undefined") {
                    this.setState({metauid: metadata.metauid[data.title]});
                }
            }
        }
    }
    checkVisibility(): void {
        const {data, subitems} = this.props;
        let newValue = data.visibleAtCurrentScale;
        if(newValue && subitems?.length){
            newValue = subitems.findIndex((i) => i.visibleAtCurrentScale)>-1;
        }
        if(this.state.visibleAtCurrentScale !== newValue) {
            this.setState(() => ({
                visibleAtCurrentScale: newValue
            }));
        }
    }
    componentDidMount(): void {
        const {data, store} = this.props;
        this.handleItemChange(data);
        this.checkVisibility();
        // rerun until map view is ready and layer list view model is loaded
        this.autorunDisposers.push(autorun((r: any) => {
            if (!store?.mapStore.view) {
                return;
            }
            this.watchHandles.push(
                store?.mapStore.view.watch("scale",()=> {
                    this.checkVisibility();
                })
            );
            r.dispose();
        }));
    }

    componentWillUnmount(): void {
        this.autorunDisposers.forEach((disposer: any) => {
            disposer();
        });
        this.autorunDisposers.length = 0;
        this.watchHandles.forEach((handle: WatchHandle) => {
            handle.remove();
        });
        this.watchHandles.length = 0;
    }

    componentDidUpdate(prevProps: any) {
        const {data} = this.props;
        if (!_.isEqual(prevProps.data?.layer, data?.layer)){
            this.handleItemChange(data);
        }
        if (this.state.open !== data?.open){
            this.setState({open: data.open});
        }
        if (this.state.visible !== data?.layer.visible){
            this.setState({visible: data.layer.visible});
        }
        if (this.state.hidden !== data?.hidden){
            this.setState({hidden: data.hidden});
        }
    }

    render(): JSX.Element {
        const {visible, visibleAtCurrentScale, open, metauid, hidden} = this.state;
        const {subitems, data, metadata, onClick, config,
            controlsConfig, level, small}
            = this.props;
        const nextLevel = level + 1;
        const showElement = (controlsConfig.layerList.startLevel <= level);
        const nextIsRoot = (controlsConfig.layerList.startLevel === nextLevel);
        const limitLevel = controlsConfig.layerList.limitLevel;
        const hasChildren = subitems && subitems.length > 0 && (limitLevel === 0 || limitLevel > nextLevel)
            && (data.layer as any)?.limitLevel !== 1;
        const metaUrl = metauid != null? metadata.urlTemplate.replace("{METAUID}", this.state.metauid): null;
        const showLayers = hasChildren && open;
        const indeterminate = visible && subitems && subitems.flatten((ite: __esri.ListItem) =>
            ite.children
        ).some((child: __esri.ListItem) => !child.visible && !child.hidden);
        const inputType = ((data.parent?.layer as any)?.visibilityMode === "exclusive")? "radio": "checkbox";
        // Das Property uid ist für ListItem nicht dokumentiert!
        // const itemUid = (data as any).uid;
        let css = "layer-list-item";
        css += visible ? " visible" : "";
        css += open ? " open" : "";
        css += hidden ? " hidden" : "";
        css += hasChildren ? " has-children" : "";
        css += visibleAtCurrentScale ? "" : " not-visible-at-scale";
        let legend;
        if(subitems?.length && visibleAtCurrentScale){
            css += subitems.findIndex((i) => i.visibleAtCurrentScale)>-1 ? "" : " not-visible-at-scale";
        }
        if (controlsConfig.legend.position === "layer" && !hasChildren) {
            const legendConfig = controlsConfig.legend;
            if (controlsConfig.legend.useEsri && controlsConfig.legend.esriConfig) {
                legend = (<Legend
                    config={controlsConfig.legend.esriConfig}
                    controlsConfig={controlsConfig}
                    keyAppend={data.layer.id}
                    layerInfos={[{layer: data.layer, title: data.title}]}
                    small={small as boolean}/>);
            } else {
                legend = (<CustomLegend
                    config={legendConfig}/>);
            }
        }
        if (showElement) {
            return (
                <li className={css}>
                    <div className="layer-list-item-container">
                        {hasChildren &&
                        <Button className="layer-list-item-toggle" icon basic size="tiny"
                            onClick={this.handleLayerToggle}>
                            <Icon name={open ? "navigate_minus" : "navigate_plus"} />
                        </Button>
                        }
                        {inputType==="radio"?
                            <Radio label={data.title}
                                checked={visible}
                                indeterminate={indeterminate}
                                onChange={(event: SyntheticEvent, eventData: object) => {
                                    this.handleCheckboxChange(event, eventData, data);
                                }} />:
                            <Checkbox label={data.title}
                                checked={visible}
                                indeterminate={indeterminate}
                                onChange={(event: SyntheticEvent, eventData: CheckboxProps) => {
                                    this.handleCheckboxChange(event, eventData, data);
                                }} />
                        }
                        {metaUrl &&
                        <Button
                            className="layer-list-item-meta" as={"a"}
                            href={metaUrl}
                            target={"_blank"}
                            icon basic size="medium" floated="right">
                            <Icon name="information" />
                        </Button>
                        }
                    </div>
                    {legend}
                    {showLayers &&
                    <LayerListList
                        listItems={(subitems)? subitems.toArray(): []}
                        onListItemClick={onClick}
                        isRoot={nextIsRoot}
                        level={nextLevel}
                        config={config}
                        small={small as boolean}
                        controlsConfig={controlsConfig} />
                    }
                </li>
            );
        } else {
            return (<LayerListList
                listItems={subitems? subitems.toArray(): []}
                onListItemClick={onClick}
                isRoot={nextIsRoot}
                level={nextLevel}
                config={config}
                small={small as boolean}
                controlsConfig={controlsConfig} />);
        }
    }
}

export const LayerListItem = withStore(LayerListItemBase);
